import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { publicidadService } from '../../../services/api';
import styles from './PublicidadTab.module.css';

const ESTADOS = ['todos', 'aprobado', 'pendiente', 'rechazado', 'archivado'];
const TIPOS   = ['todos', 'imagen', 'video', 'audio', 'html', 'pdf'];

const ESTADO_COLORS = {
  aprobado:  styles.badgeAprobado,
  pendiente: styles.badgePendiente,
  rechazado: styles.badgeRechazado,
  archivado: styles.badgeInactivo,
};

export default function BibliotecaTab() {
  const [pautas,     setPautas]     = useState([]);
  const [filtroAnunciante, setFiltroAnunciante] = useState('');
  const [filtroEstado,     setFiltroEstado]     = useState('todos');
  const [filtroTipo,       setFiltroTipo]       = useState('todos');
  const [busqueda,         setBusqueda]         = useState('');
  const [reactivando,      setReactivando]      = useState(null);
  const [asignando,        setAsignando]        = useState(null);
  const [msg,              setMsg]              = useState(null);

  const load = useCallback(async () => {
    const data = await publicidadService.getPautas().catch(() => []);
    // Cargar detalle de cada pauta para obtener materiales
    const detalles = await Promise.all(
      (data || []).map(p => publicidadService.getPauta(p.id).catch(() => null))
    );
    setPautas(detalles.filter(Boolean));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Aplanar todos los materiales de todas las pautas
  const todosMateriales = pautas.flatMap(p =>
    (p.materiales || []).map(m => ({
      ...m,
      pautaId:          p.id,
      anuncianteNombre: p.anunciante?.razonSocial || '—',
      pautaEstado:      p.estado,
    }))
  );

  // Anunciantes únicos para el filtro
  const anunciantesUnicos = [...new Set(todosMateriales.map(m => m.anuncianteNombre))].sort();

  // Aplicar filtros
  const materiales = todosMateriales.filter(m => {
    if (filtroAnunciante && m.anuncianteNombre !== filtroAnunciante) return false;
    if (filtroEstado !== 'todos' && m.estadoRevision !== filtroEstado) return false;
    if (filtroTipo !== 'todos' && m.tipoArchivo !== filtroTipo) return false;
    if (busqueda && !m.nombre.toLowerCase().includes(busqueda.toLowerCase()) &&
        !m.anuncianteNombre.toLowerCase().includes(busqueda.toLowerCase())) return false;
    return true;
  });

  const reactivar = async (mat) => {
    if (!window.confirm(`¿Reactivar "${mat.nombre}"? Quedará en estado pendiente de aprobación en la pauta #${mat.pautaId}.`)) return;
    setReactivando(mat.id);
    try {
      await publicidadService.revisarMaterial(mat.id, { estadoRevision: 'pendiente', observacionesRevision: 'Reactivado desde Biblioteca de materiales' });
      setMsg({ type: 'ok', text: `"${mat.nombre}" reactivado. Ve a la pauta #${mat.pautaId} para aprobarlo.` });
      await load();
    } catch(e) { setMsg({ type: 'error', text: 'Error: ' + e.message }); }
    setReactivando(null);
  };

  const marcarPrincipal = async (mat) => {
    if (mat.estadoRevision !== 'aprobado') {
      setMsg({ type: 'error', text: 'Solo se puede marcar como principal un material aprobado.' });
      return;
    }
    setAsignando(mat.id);
    try {
      await publicidadService.marcarPrincipal(mat.pautaId, mat.id);
      setMsg({ type: 'ok', text: `"${mat.nombre}" marcado como principal en la pauta #${mat.pautaId}.` });
      await load();
    } catch(e) { setMsg({ type: 'error', text: 'Error: ' + e.message }); }
    setAsignando(null);
  };

  const renderPreview = (m) => {
    if (m.tipoArchivo === 'imagen') {
      return (
        <img src={m.urlStorage} alt={m.nombre}
          style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, background: '#f0f0f0' }}
          onError={e => { e.target.style.display = 'none'; }} />
      );
    }
    if (m.tipoArchivo === 'video') {
      return (
        <video src={m.urlStorage} muted
          style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, background: '#111' }} />
      );
    }
    const iconos = { audio: '🎵', html: '🌐', pdf: '📄' };
    return (
      <div style={{ width: '100%', height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#f5f5f5', borderRadius: 4, fontSize: 32 }}>
        {iconos[m.tipoArchivo] || '📎'}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <strong style={{ fontSize: 15 }}>Biblioteca de materiales</strong>
        <span style={{ fontSize: 12, color: '#888' }}>— {todosMateriales.length} archivos en total</span>
        <button className={styles.btnOutline} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px' }}
          onClick={load}>
          <RefreshCw size={12} style={{ marginRight: 4 }} />Actualizar
        </button>
      </div>

      {msg && (
        <div className={`${styles.msg} ${msg.type === 'ok' ? styles.msgOk : styles.msgError}`}
          style={{ marginBottom: 12 }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
          <input placeholder='Buscar por nombre o anunciante...' value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <select value={filtroAnunciante} onChange={e => setFiltroAnunciante(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
          <option value=''>Todos los anunciantes</option>
          {anunciantesUnicos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
          {ESTADOS.map(e => <option key={e} value={e}>{e === 'todos' ? 'Todos los estados' : e}</option>)}
        </select>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
          {TIPOS.map(t => <option key={t} value={t}>{t === 'todos' ? 'Todos los tipos' : t}</option>)}
        </select>
      </div>

      {/* Resultados */}
      {materiales.length === 0 ? (
        <div className={styles.empty}>No hay materiales con esos filtros.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {materiales.map(m => (
            <div key={`${m.pautaId}-${m.id}`} style={{
              border: '1px solid #eee', borderRadius: 8, overflow: 'hidden',
              background: m.estadoRevision === 'archivado' ? '#fafafa' : '#fff',
              opacity: m.estadoRevision === 'archivado' ? 0.75 : 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Preview */}
              <div style={{ cursor: 'pointer' }} onClick={() => window.open(m.urlStorage, '_blank', 'noopener')}>
                {renderPreview(m)}
              </div>

              {/* Info */}
              <div style={{ padding: '8px 10px', flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={m.nombre}>{m.nombre}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                  {m.anuncianteNombre}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                  <span className={`${styles.badge} ${ESTADO_COLORS[m.estadoRevision] || styles.badgePendiente}`}>
                    {m.estadoRevision}
                  </span>
                  <span style={{ fontSize: 10, color: '#aaa' }}>Pauta #{m.pautaId}</span>
                  {m.esPrincipal && <span style={{ fontSize: 10 }}>⭐</span>}
                </div>
              </div>

              {/* Acciones */}
              <div style={{ padding: '6px 10px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <a href={m.urlStorage} target='_blank' rel='noreferrer' className={styles.btnIcon}
                  style={{ fontSize: 11 }}>Ver</a>

                {m.estadoRevision === 'archivado' && (
                  <button className={styles.btnIcon} disabled={reactivando === m.id}
                    style={{ fontSize: 11, color: '#1B4332', borderColor: '#1B4332' }}
                    onClick={() => reactivar(m)}
                    title='Volver a poner en pendiente de aprobación'>
                    {reactivando === m.id ? '...' : '↺ Reactivar'}
                  </button>
                )}

                {m.estadoRevision === 'aprobado' && !m.esPrincipal && (
                  <button className={styles.btnIcon} disabled={asignando === m.id}
                    style={{ fontSize: 11, color: '#1B4332', borderColor: '#1B4332' }}
                    onClick={() => marcarPrincipal(m)}
                    title='Marcar como principal en su pauta'>
                    {asignando === m.id ? '...' : '⭐ Principal'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
